import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const InteractiveBackground = ({ mousePosition }) => {
  const containerRef = useRef(null);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Create interactive particles that follow mouse
    const createParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 20; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: Math.random() * 4 + 2,
          color: `hsl(${200 + Math.random() * 60}, 70%, 60%)`,
          vx: (Math.random() - 0.5) * 2,
          vy: (Math.random() - 0.5) * 2,
        });
      }
      setParticles(newParticles);
    };

    createParticles();
  }, []);

  return (
    <div ref={containerRef} className="fixed inset-0 pointer-events-none z-0">
      {/* Mouse-following light effect */}
      <motion.div
        className="absolute w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, 
            rgba(14, 165, 233, 0.15) 0%, 
            rgba(139, 92, 246, 0.1) 30%, 
            transparent 70%
          )`,
          filter: 'blur(20px)',
        }}
        animate={{
          x: mousePosition.x - 192,
          y: mousePosition.y - 192,
        }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 200,
        }}
      />

      {/* Constellation effect */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {particles.map((particle, index) => (
          <g key={particle.id}>
            {/* Particle */}
            <motion.circle
              cx={particle.x}
              cy={particle.y}
              r={particle.size}
              fill={particle.color}
              filter="url(#glow)"
              animate={{
                cx: particle.x + Math.sin(Date.now() * 0.001 + index) * 50,
                cy: particle.y + Math.cos(Date.now() * 0.001 + index) * 30,
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            
            {/* Connection lines */}
            {particles.slice(index + 1).map((otherParticle, otherIndex) => {
              const distance = Math.sqrt(
                Math.pow(particle.x - otherParticle.x, 2) + 
                Math.pow(particle.y - otherParticle.y, 2)
              );
              
              if (distance < 150) {
                return (
                  <motion.line
                    key={`${particle.id}-${otherParticle.id}`}
                    x1={particle.x}
                    y1={particle.y}
                    x2={otherParticle.x}
                    y2={otherParticle.y}
                    stroke="rgba(14, 165, 233, 0.2)"
                    strokeWidth="1"
                    animate={{
                      opacity: [0, 0.5, 0],
                    }}
                    transition={{
                      duration: 3,
                      delay: index * 0.1,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                );
              }
              return null;
            })}
          </g>
        ))}
      </svg>

      {/* Ripple effects */}
      <div className="absolute inset-0">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`ripple-${i}`}
            className="absolute border border-primary-400/30 rounded-full"
            style={{
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            animate={{
              width: [0, 600],
              height: [0, 600],
              opacity: [0.5, 0],
            }}
            transition={{
              duration: 4,
              delay: i * 1.3,
              repeat: Infinity,
              ease: "easeOut",
            }}
          />
        ))}
      </div>

      {/* Floating tech icons */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={`tech-${i}`}
          className="absolute w-8 h-8 opacity-20"
          style={{
            left: `${10 + i * 15}%`,
            top: `${20 + (i % 3) * 30}%`,
          }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 360],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 6 + i,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <div 
            className="w-full h-full bg-gradient-to-br from-primary-400 to-accent-purple rounded"
            style={{
              clipPath: i % 3 === 0 
                ? 'polygon(50% 0%, 0% 100%, 100% 100%)' 
                : i % 3 === 1 
                ? 'polygon(20% 0%, 80% 0%, 100% 20%, 100% 80%, 80% 100%, 20% 100%, 0% 80%, 0% 20%)'
                : 'circle(50%)',
            }}
          />
        </motion.div>
      ))}
    </div>
  );
};

export default InteractiveBackground;